import { cancelOnSessionChange } from '../session-cancellation';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NGXLogger } from 'ngx-logger';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganizationsService } from '@api/services/organizations.service';
import * as MarkdownsActions from '../../actions/organizations/markdowns.actions';
import { MessageService } from 'primeng/api';

@Injectable()
export class OrganizationsMarkdownsEffects {
  checkMarkdownAndCreateVersion$;
  updateMarkdownVersion$;
  deleteMarkdownVersion$;
  getMarkdownValidationResults$;
  updateMarkdownVersionSuccess$;
  updateMarkdownVersionFailure$;
  deleteMarkdownVersionSuccess$;
  deleteMarkdownVersionFailure$;
  reorderMarkdowns$;

  constructor(
    private logger: NGXLogger,
    private actions$: Actions,
    private organizationsService: OrganizationsService,
    private messageService: MessageService
  ) {
    this.checkMarkdownAndCreateVersion$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MarkdownsActions.checkMarkdownAndCreateVersion),
        mergeMap(({ organizationId, projectId, uploadIntentId, versionName }) =>
          this.organizationsService.organizationsCheckMarkdownAndCreateVersion({
            organizationId,
            projectId,
            body: {
              uploadIntentId,
              versionName
            }
          }).pipe(
            map(({ project, results }) => {
              return MarkdownsActions.checkMarkdownAndCreateVersionSuccess({
                project: { data: project!, loadedAt: new Date()},
                uploadIntentId,
                results: results || []
              });
            }),
            catchError((error) => of(MarkdownsActions.checkMarkdownAndCreateVersionFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.getMarkdownValidationResults$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MarkdownsActions.getMarkdownValidationResults),
        mergeMap(({ organizationId, projectId, uploadIntentId }) =>
          this.organizationsService.organizationsGetMarkdownValidationResults({
            organizationId,
            projectId,
            uploadIntentId
          }).pipe(
            map(({ project, results }) =>
              MarkdownsActions.getMarkdownValidationResultsSuccess({
                project: { data: project!, loadedAt: new Date() },
                uploadIntentId,
                results: results || []
              })
            ),
            catchError(error => of(MarkdownsActions.getMarkdownValidationResultsFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.updateMarkdownVersion$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MarkdownsActions.updateMarkdownVersion),
        mergeMap(({ organizationId, projectId, uploadIntentId, versionName, status }) =>
          this.organizationsService.organizationsUpdateMarkdownVersion({
            organizationId,
            projectId,
            uploadIntentId,
            body: { versionName, status }
          }).pipe(
            map(({ project, markdown }) =>
              MarkdownsActions.updateMarkdownVersionSuccess({
                project: { data: project!, loadedAt: new Date() },
                markdown: markdown!
              })
            ),
            catchError(error => of(MarkdownsActions.updateMarkdownVersionFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.deleteMarkdownVersion$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MarkdownsActions.deleteMarkdownVersion),
        mergeMap(({ organizationId, projectId, uploadIntentId }) =>
          this.organizationsService.organizationsDeleteMarkdownVersion({
            organizationId,
            projectId,
            uploadIntentId
          }).pipe(
            map(({ project, markdown }) =>
              MarkdownsActions.deleteMarkdownVersionSuccess({
                project: { data: project!, loadedAt: new Date() },
                markdown: markdown!
              })
            ),
            catchError(error => of(MarkdownsActions.deleteMarkdownVersionFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.reorderMarkdowns$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MarkdownsActions.reorderMarkdowns),
        mergeMap(({ organizationId, projectId, order }) =>
          this.organizationsService.organizationsReorderMarkdowns(            
            {
              organizationId,
              projectId,
              body: { order }
            }
          ).pipe(
            map(response =>
              MarkdownsActions.reorderMarkdownsSuccess({
                project: { data: response.project!, loadedAt: new Date() }
              })
            ),
            catchError(error => of(MarkdownsActions.reorderMarkdownsFailure({ error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.updateMarkdownVersionSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MarkdownsActions.updateMarkdownVersionSuccess),
        tap(({ project }) => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Markdown updated successfully.' });
          this.logger.info('Markdown updated successfully', project);
        })
      ),
      { dispatch: false }
    );

    this.updateMarkdownVersionFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MarkdownsActions.updateMarkdownVersionFailure),
        tap(({ error }) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Update markdown failed.' });
          this.logger.error('Update markdown version failed', error);
        })
      ),
      { dispatch: false }
    );

    this.deleteMarkdownVersionSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MarkdownsActions.deleteMarkdownVersionSuccess),
        tap(({ project }) => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Markdown deleted successfully.' });
          this.logger.info('Markdown deleted successfully', project);
        })
      ),
      { dispatch: false }
    );

    this.deleteMarkdownVersionFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(MarkdownsActions.deleteMarkdownVersionFailure),
        tap(({ error }) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Delete markdown failed.' });
          this.logger.error('Delete markdown version failed', error);
        })
      ),
      { dispatch: false }
    );
  }
}